<template>
  <button
    class="lg-btn"
    :class="[`v-${variant}`, `s-${size}`, { block }]"
    :type="type"
    :disabled="disabled"
  >
    <slot name="icon" />
    <span v-if="$slots.default" class="lg-btn-label"><slot /></span>
  </button>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    variant?: 'primary' | 'ghost' | 'danger';
    size?: 'sm' | 'md';
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    block?: boolean;
  }>(),
  { variant: 'ghost', size: 'md', type: 'button', disabled: false, block: false },
);
</script>

<style scoped>
.lg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  font-family: var(--lg-font-sans);
  font-weight: 600;
  border-radius: var(--lg-radius-md);
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background var(--lg-dur-fast) var(--lg-ease),
    border-color var(--lg-dur-fast) var(--lg-ease),
    filter var(--lg-dur-fast) var(--lg-ease);
}
.lg-btn.block {
  width: 100%;
}
.lg-btn:disabled {
  opacity: 0.55;
  cursor: default;
}
.lg-btn:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}

/* sizes */
.s-sm {
  min-height: var(--lg-control-sm);
  padding: 0 var(--lg-space-3);
  font-size: var(--lg-fs-sm);
}
.s-md {
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-4);
  font-size: var(--lg-fs-md);
}

/* primary */
.v-primary {
  background: var(--lg-accent);
  color: var(--lg-accent-fg);
}
.v-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

/* ghost */
.v-ghost {
  background: var(--lg-bg-surface);
  border-color: var(--lg-border-strong);
  color: var(--lg-text);
}
.v-ghost:hover:not(:disabled) {
  border-color: var(--lg-accent);
  background: var(--lg-bg-hover);
}

/* danger */
.v-danger {
  background: transparent;
  border-color: var(--lg-border-strong);
  color: var(--lg-danger);
}
.v-danger:hover:not(:disabled) {
  border-color: var(--lg-danger);
  background: color-mix(in srgb, var(--lg-danger) 12%, transparent);
}
</style>
